import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/custom/Dialog';
import { Conversation } from '@/services/omnichannelService';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { useMetadata } from '@/hooks/useMetadata';
import { groupAgentsService } from '@/services/groupAgentsService';
import { userService } from '@/services/userService';
import { formTemplateService, type FormTemplate } from '@/services/formTemplateService';
import { httpClient } from '@/services/httpClient';
import { PrioritySelector } from '../customs/PrioritySelector';
import { Priority } from '@/types';
import { Tabs } from '@/components/common/Tabs';

interface FormSubmission {
  id: string;
  conversation_id: string;
  form_submission: Record<string, any>;
  submitted_at: string;
  customer_name: string;
  customer_email: string;
  sender_name: string;
}

interface TicketEscalationDialogProps {
  conversation: Conversation | null;
  isOpen: boolean;
  onClose: () => void;
  onCreateTicket: (ticketData: {
    title: string;
    description: string;
    priority: string;
    status: string;
    group_id?: number;
    assigned_to_id?: number;
    form_template_id?: number;
    form_response?: any;
  }) => Promise<void>;
}

export default function TicketEscalationDialog({
  conversation,
  isOpen,
  onClose,
  onCreateTicket
}: TicketEscalationDialogProps) {
  const { theme } = useTheme();
  const { groups } = useMetadata();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: 'new',
    group_id: '',
    assigned_to_id: '',
    form_template_id: '',
    form_response: {}
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [groupAgents, setGroupAgents] = useState<{ value: string; label: string }[]>([]);
  const [formTemplates, setFormTemplates] = useState<FormTemplate[]>([]);
  const [formSubmissions, setFormSubmissions] = useState<FormSubmission[]>([]);
  const [selectedFormSubmission, setSelectedFormSubmission] = useState<FormSubmission | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplate | null>(null);
  const [isLoadingFormSubmissions, setIsLoadingFormSubmissions] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [activeTab, setActiveTab] = useState<'description' | 'form'>('description');

  // Pre-fill form data when conversation changes
  useEffect(() => {
    if (conversation) {
      setFormData((prev) => ({
        ...prev,
        title: conversation.subject || 'New Ticket',
        description: 'Created from conversation',
        priority: conversation.priority || 'medium',
        status: 'new'
      }));
    }
  }, [conversation]);

  // Load form templates when dialog opens
  useEffect(() => {
    if (isOpen && conversation) {
      loadFormTemplates();
      loadFormSubmissions();
    }
  }, [isOpen, conversation]);

  // Load agents when group changes
  useEffect(() => {
    if (formData.group_id) {
      loadGroupAgents(formData.group_id);
    } else {
      setGroupAgents([]);
    }
  }, [formData.group_id]);

  // Load form submissions for this conversation
  const loadFormSubmissions = async () => {
    if (!conversation) return;

    setIsLoadingFormSubmissions(true);
    try {
      const response = await httpClient.get<any>(
        `/api/channels/conversations/${conversation.id}/form-messages`
      );
      console.log('form submissions response:', response);
      setFormSubmissions(response.data || []);
    } catch (error) {
      console.error('Error loading form submissions:', error);
      setFormSubmissions([]);
    } finally {
      setIsLoadingFormSubmissions(false);
    }
  };

  // Load form templates
  const loadFormTemplates = async () => {
    setIsLoadingTemplates(true);
    try {
      const templates = await formTemplateService.getFormTemplates();
      if (Array.isArray(templates)) {
        setFormTemplates(templates);
      }
    } catch (error) {
      console.error('Error loading form templates:', error);
      setFormTemplates([]);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  // Load agents for a specific group
  const loadGroupAgents = async (groupId: string) => {
    try {
      const agents = await groupAgentsService.getAgentsByGroupId(parseInt(groupId));
      const agentDetails = await Promise.all(
        agents.map(async (agent) => {
          const user = await userService.getUser(agent.user_id);
          return {
            value: user.id.toString(),
            label: `${user.full_name} (${user.email})`
          };
        })
      );
      setGroupAgents(agentDetails);
    } catch (error) {
      console.error('Error loading group agents:', error);
      setGroupAgents([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare ticket data with proper types based on active tab
      const ticketData: any = {
        title: formData.title,
        priority: formData.priority,
        status: formData.status,
        group_id: formData.group_id ? parseInt(formData.group_id) : undefined,
        assigned_to_id:
          formData.assigned_to_id && formData.assigned_to_id !== 'ANY_AGENT'
            ? parseInt(formData.assigned_to_id)
            : undefined
      };

      if (activeTab === 'description') {
        ticketData.description = formData.description;
      } else if (activeTab === 'form' && selectedFormSubmission && selectedTemplate) {
        ticketData.form_template_id = selectedTemplate.id;
        ticketData.form_response = selectedFormSubmission.form_submission;
      }

      await onCreateTicket(ticketData);
      onClose();
      // Reset form
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        status: 'new',
        group_id: '',
        assigned_to_id: '',
        form_template_id: '',
        form_response: {}
      });
      setSelectedFormSubmission(null);
      setSelectedTemplate(null);
      setActiveTab('description');
    } catch (error) {
      console.error('Failed to create ticket:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string | any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTabChange = (tabId: string) => {
    if (tabId === 'description' || tabId === 'form') {
      setActiveTab(tabId);
    }
  };

  const handleFormSubmissionSelect = (submission: FormSubmission) => {
    setSelectedFormSubmission(submission);
    // Find the template that matches this submission's channel metadata
    if (submission.form_submission) {
      // You might need to match based on form template ID if available
      const matchingTemplate = formTemplates.find(
        (template) =>
          submission.form_submission && Object.keys(submission.form_submission).length > 0
      );
      setSelectedTemplate(matchingTemplate || null);
    }
  };

  if (!conversation) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`sm:max-w-screen-md w-full ${theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white'}`}
      >
        <DialogHeader>
          <DialogTitle>Create Ticket from Conversation</DialogTitle>
          <DialogDescription>
            Create a support ticket from this conversation with the customer.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 max-h-[80vh] overflow-y-auto">
          {/* Priority */}
          <div>
            <label
              className={`block text-sm font-medium mb-1 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              Ticket Priority
            </label>
            <PrioritySelector
              value={formData.priority as Priority}
              onChange={(value) => handleInputChange('priority', value)}
              className={theme === 'dark' ? 'dark' : ''}
            />
          </div>

          {/* Basic Information */}
          <div>
            <label
              htmlFor="title"
              className={`block text-sm font-medium mb-1 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              Title
            </label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className={`w-full px-3 py-2 rounded-md border ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter ticket title"
              required
            />
          </div>

          {/* Assignment */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="group_id"
                className={`block text-sm font-medium mb-1 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                Assign to Group
              </label>
              <select
                id="group_id"
                value={formData.group_id}
                onChange={(e) => {
                  handleInputChange('group_id', e.target.value);
                  handleInputChange('assigned_to_id', ''); // Reset assignee when group changes
                }}
                className={`w-full px-3 py-2 rounded-md border ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                <option value="">Select Group</option>
                {groups?.map((group) => (
                  <option key={group.value} value={group.value}>
                    {group.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="assigned_to_id"
                className={`block text-sm font-medium mb-1 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                Assign to Agent
              </label>
              <select
                id="assigned_to_id"
                value={formData.assigned_to_id}
                onChange={(e) => handleInputChange('assigned_to_id', e.target.value)}
                disabled={!formData.group_id || groupAgents.length === 0}
                className={`w-full px-3 py-2 rounded-md border ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  !formData.group_id || groupAgents.length === 0 ? 'opacity-50' : ''
                }`}
              >
                <option value="">Select Agent</option>
                <option value="ANY_AGENT">Any Agent</option>
                {groupAgents.map((agent) => (
                  <option key={agent.value} value={agent.value}>
                    {agent.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Tabs for Description and Form Submissions */}
          <div>
            <Tabs
              tabs={[
                { id: 'description', label: 'Description' },
                ...(formSubmissions.length > 0 ? [{ id: 'form', label: 'Form Submission' }] : [])
              ]}
              activeTab={activeTab}
              onChange={handleTabChange}
            />

            {/* Tab Content */}
            <div className="mt-4">
              {activeTab === 'description' && (
                <div>
                  <label
                    htmlFor="description"
                    className={`block text-sm font-medium mb-1 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    Description
                  </label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className={`w-full px-3 py-2 rounded-md border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="Enter ticket description"
                    required
                  />
                </div>
              )}

              {activeTab === 'form' && (
                <div>
                  <label
                    className={`block text-sm font-medium mb-1 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    Customer Form Submissions
                  </label>
                  {isLoadingFormSubmissions ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                      <span className="ml-2 text-sm">Loading submissions...</span>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-32 overflow-y-auto border rounded-md p-2">
                      {formSubmissions.map((submission) => (
                        <div
                          key={submission.id}
                          className={`p-2 rounded cursor-pointer border ${
                            selectedFormSubmission?.id === submission.id
                              ? theme === 'dark'
                                ? 'bg-blue-900 border-blue-500'
                                : 'bg-blue-50 border-blue-300'
                              : theme === 'dark'
                                ? 'bg-gray-700 border-gray-600 hover:bg-gray-600'
                                : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                          }`}
                          onClick={() => handleFormSubmissionSelect(submission)}
                        >
                          <div className="text-sm font-medium">
                            {submission.sender_name || 'Customer'}
                          </div>
                          <div className="text-xs opacity-75">
                            {new Date(submission.submitted_at).toLocaleString()}
                          </div>
                          <div className="text-xs opacity-75">
                            {Object.keys(submission.form_submission || {}).length} fields
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Form Template Selection */}
          {selectedFormSubmission && (
            <div>
              <label
                className={`block text-sm font-medium mb-1 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                Form Template
              </label>
              {isLoadingTemplates ? (
                <div className="flex items-center justify-center py-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                  <span className="ml-2 text-sm">Loading templates...</span>
                </div>
              ) : (
                <input
                  type="text"
                  value={selectedTemplate?.name || ''}
                  readOnly
                  className={`w-full px-3 py-2 rounded-md border ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              )}
            </div>
          )}

          <div
            className={`flex justify-end gap-3 pt-4 border-t ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}
          >
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSubmitting ? 'Creating...' : 'Create Ticket'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
